import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../components/ui/form";
import Button from "../../../components/common/button";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { updateFirestoreDoc } from "../../../api";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { db } from "../../../config/firebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchBankLeadData } from "../../../api";
import toast from "react-hot-toast";
import { useAuthData } from "../../../context/AuthContext";

const formSchema = z.object({
  plot: z.object({
    address: z.string().min(1, "Adresse er påkrevd"),
    Postnummer: z.string().min(1, "Postnummer påkrevd"),
    Poststed: z.string().min(1, "Poststed påkrevd"),
    alreadyHavePlot: z.string().min(1, "Påkrevd"),
    Kommentar: z.string().min(1, {
      message: "Kommentar til banken må bestå av minst 1 tegn.",
    }),
    tomtekostnader: z.string().min(1, {
      message: "Estimert totale tomtekostnader må bestå av minst 1 tegn.",
    }),
  }),
  house: z.object({
    housemodell: z
      .string()
      .min(1, { message: "Housemodell must må spesifiseres." }),
    Kommentar: z.string().min(1, {
      message: "Kommentar til banken må bestå av minst 1 tegn.",
    }),
    byggekostnader: z.string().min(1, {
      message: "Estimert totale byggekostnader må bestå av minst 1 tegn.",
    }),
  }),
});

export type PlotHusmodellHandle = {
  validateForm: () => Promise<boolean>;
};

export const PlotHusmodell = forwardRef<
  PlotHusmodellHandle,
  { setActiveTab: (tab: number) => void }
>(({ setActiveTab }, ref) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathSegments = location.pathname.split("/");
  const id = pathSegments.length > 2 ? pathSegments[2] : null;
  const form = useForm<any>({
    resolver: zodResolver(formSchema),
  });
  const { adminData } = useAuthData();

  const selectedPlotType = form.watch("plot.alreadyHavePlot");

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      const BankData = {
        ...data,
        id: id,
      };
      const formatDate = (date: Date) => {
        return date
          .toLocaleString("sv-SE", { timeZone: "UTC" })
          .replace(",", "");
      };

      await updateFirestoreDoc(db, "bank_leads", String(id), {
        plotHusmodell: BankData,
        updatedAt: formatDate(new Date()),
      });
      toast.success("Lagret", { position: "top-right" });

      navigate(`/edit-bank-leads/${id}`);
      setActiveTab(2);
    } catch (error) {
      console.error("Firestore operation failed:", error);
      toast.error("Something went wrong. Please try again.", {
        position: "top-right",
      });
    }
  };

  const [email, setEmail] = useState<any>(null);
  const [permission, setPermission] = useState<any>(null);
  const [role, setRole] = useState<any>(null);

  useEffect(() => {
    const getData = async () => {
      if (adminData?.role) {
        setRole(adminData?.role);
      }
      if (adminData) {
        setEmail(adminData?.email);
      }
      if (adminData && adminData?.supplier) {
        const finalData = adminData?.supplier;
        setPermission(finalData);
      } else {
        setPermission(null);
      }
    };

    getData();
  }, [permission, adminData]);

  const [house, setHouse] = useState([]);

  const fetchHusmodellData = async () => {
    try {
      let q;
      if (email === "andre.finger@gmail.com" || role === "Admin") {
        q = query(collection(db, "house_model"), orderBy("updatedAt", "desc"));
      } else {
        if (permission || permission !== null) {
          q = query(
            collection(db, "house_model"),
            where("Husdetaljer.Leverandører", "==", permission)
          );
        } else {
          q = query(
            collection(db, "house_model"),
            orderBy("updatedAt", "desc")
          );
        }
      }

      const querySnapshot = await getDocs(q);

      const data: any = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setHouse(data);
    } catch (error) {
      console.error("Error fetching husmodell data:", error);
    }
  };

  useEffect(() => {
    fetchHusmodellData();
  }, [permission]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const getData = async () => {
      const data = await fetchBankLeadData(id);

      if (data && data.plotHusmodell) {
        Object.entries(data.plotHusmodell).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            form.setValue(key as any, value);
          }
        });
      }
    };

    getData();
  }, [form, id, house]);

  useImperativeHandle(ref, () => ({
    validateForm: async () => {
      let isValid = false;

      await form.handleSubmit(async (data) => {
        await onSubmit(data);
        isValid = true;
      })();

      return isValid;
    },
  }));
  const alreadyHavePlotError =
    form.formState.errors?.plot &&
    "alreadyHavePlot" in form.formState.errors.plot &&
    form.formState.errors.plot.alreadyHavePlot;
  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
          <div className="mb-20">
            <div className="rounded-lg bg-lightGreen mb-6 w-full">
              <div className="p-3 md:p-4">
                <h3 className="text-base lg:text-lg font-semibold text-black3 mb-2">
                  Registrering av prosjekt
                </h3>
                <p className="text-sm text-gray4">
                  Her fyller du inn informasjon om prosjektet.
                </p>
              </div>
              <div className="p-1 pt-0">
                <div className="flex flex-col gap-4 lg:gap-6 bg-white p-4 rounded-lg border border-gray3 shadow-shadow4">
                  <div className="flex flex-col md:grid md:grid-cols-2 desktop:grid-cols-3 gap-4 lg:gap-6">
                    <div>
                      <FormField
                        control={form.control}
                        name="plot.address"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <p
                              className={`${
                                fieldState.error ? "text-red" : "text-black"
                              } mb-[6px] text-sm font-medium`}
                            >
                              Adresse for prosjektet
                            </p>
                            <FormControl>
                              <div className="relative">
                                <div className="flex items-center gap-2">
                                  <Input
                                    placeholder="Skriv inn Adresse for prosjektet"
                                    {...field}
                                    className={`bg-white truncate pr-10 rounded-[8px] border text-black
                                          ${
                                            fieldState?.error
                                              ? "border-red"
                                              : "border-gray1"
                                          } `}
                                    type="text"
                                  />
                                </div>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div>
                      <FormField
                        control={form.control}
                        name="plot.Postnummer"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <p
                              className={`${
                                fieldState.error ? "text-red" : "text-black"
                              } mb-[6px] text-sm font-medium`}
                            >
                              Postnummer
                            </p>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  placeholder="Skriv inn postnummer"
                                  {...field}
                                  className={`bg-white rounded-[8px] border text-black
                                          ${
                                            fieldState?.error
                                              ? "border-red"
                                              : "border-gray1"
                                          } `}
                                  type="text"
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div>
                      <FormField
                        control={form.control}
                        name="plot.Poststed"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <p
                              className={`${
                                fieldState.error ? "text-red" : "text-black"
                              } mb-[6px] text-sm font-medium`}
                            >
                              Poststed
                            </p>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  placeholder="Skriv inn poststed"
                                  {...field}
                                  className={`bg-white rounded-[8px] border text-black
                                          ${
                                            fieldState?.error
                                              ? "border-red"
                                              : "border-gray1"
                                          } `}
                                  type="text"
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 md:gap-5">
                      <p
                        className={`font-medium text-black3 text-sm md:text-base`}
                      >
                        Eier kunden tomten allerede?
                      </p>
                      {/* <div className="flex gap-2 md:gap-4 items-center">
                        {[
                          {
                            label: "Ja",
                            value: "Ja",
                          },
                          {
                            label: "Nei",
                            value: "Nei",
                          },
                        ].map((item: any, index: number) => (
                          <div
                            key={index}
                            onClick={() => {
                              form.setValue("plot.alreadyHavePlot", item.value);
                            }}
                            className={`border-2 rounded-lg p-[10px] w-[80px] md:w-[106px] cursor-pointer ${
                              selectedPlotType === item.value
                                ? "border-primary"
                                : "border-transparent"
                            }`}
                            style={{
                              boxShadow:
                                selectedPlotType === item.value
                                  ? ""
                                  : "0px 1px 4px 0px #27201D26",
                            }}
                          >
                            <div
                              className={`${
                                selectedPlotType === item.value
                                  ? "font-medium"
                                  : "text-darkGray font-normal"
                              } text-sm text-center font-medium`}
                            >
                              {item.label}
                            </div>
                          </div>
                        ))}
                      </div> */}
                      <div className="flex gap-2 md:gap-4 items-center">
                        {[
                          { label: "Ja", value: "Ja" },
                          { label: "Nei", value: "Nei" },
                        ].map((item, index) => (
                          <label
                            key={index}
                            className={`flex items-center cursor-pointer transition-all gap-2`}
                          >
                            <input
                              type="radio"
                              name="alreadyHavePlot"
                              value={item.value}
                              checked={selectedPlotType === item.value}
                              onChange={() =>
                                form.setValue(
                                  "plot.alreadyHavePlot",
                                  item.value
                                )
                              }
                              className="accent-primary h-5 w-5"
                            />
                            <div className={`text-sm text-center text-black3`}>
                              {item.label}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {alreadyHavePlotError &&
                      !form.watch("plot.alreadyHavePlot") && (
                        <div className="text-red text-sm mt-2">
                          {(alreadyHavePlotError as any).message}
                        </div>
                      )}
                  </div>
                  <div className="flex flex-col md:grid md:grid-cols-2 desktop:grid-cols-3 md:gap-6">
                    <div>
                      <FormField
                        control={form.control}
                        name="plot.tomtekostnader"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <p
                              className={`${
                                fieldState.error ? "text-red" : "text-black"
                              } mb-[6px] text-sm font-medium`}
                            >
                              Estimert{" "}
                              <span className="text-darkBlack font-bold">
                                totale tomtekostnader
                              </span>{" "}
                              (kjøp + klargjøring):
                            </p>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  placeholder="Skriv her..."
                                  {...field}
                                  className={`bg-white rounded-[8px] border text-black
                                          ${
                                            fieldState?.error
                                              ? "border-red"
                                              : "border-gray1"
                                          } `}
                                  type="text"
                                  inputMode="numeric"
                                  onChange={({ target: { value } }: any) =>
                                    field.onChange({
                                      target: {
                                        name: `tomtekostnader`,
                                        value: value.replace(/\D/g, "")
                                          ? new Intl.NumberFormat(
                                              "no-NO"
                                            ).format(
                                              Number(value.replace(/\D/g, ""))
                                            )
                                          : "",
                                      },
                                    })
                                  }
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="w-full col-span-2">
                      <FormField
                        control={form.control}
                        name="plot.Kommentar"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <p
                              className={`${
                                fieldState.error ? "text-red" : "text-black"
                              } mb-[6px] text-sm font-medium`}
                            >
                              Kommentar til banken:
                            </p>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  placeholder="Skriv her..."
                                  {...field}
                                  className={`bg-white rounded-[8px] border text-black
                                          ${
                                            fieldState?.error
                                              ? "border-red"
                                              : "border-gray1"
                                          } `}
                                  type="text"
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-lg bg-lightGreen">
              <div className="p-3 md:p-4">
                <h3 className="text-base lg:text-lg font-semibold text-black3 mb-2">
                  Informasjon om husmodellen
                </h3>
                <p className="text-sm text-gray4">
                  Her velger du ønsket husmodell, alternativt velger “Spesial”
                </p>
              </div>
              <div className="p-1 pt-0">
                <div className="flex flex-col gap-4 lg:gap-6 bg-white p-4 rounded-lg border border-gray3 shadow-shadow4">
                  <div className="flex flex-col md:grid md:grid-cols-2 desktop:grid-cols-3 gap-4 lg:gap-6">
                    <div>
                      <FormField
                        control={form.control}
                        name="house.housemodell"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <p
                              className={`${
                                fieldState.error ? "text-red" : "text-black"
                              } mb-[6px] text-sm font-medium`}
                            >
                              Velg husmodell
                            </p>
                            <FormControl>
                              <div className="relative">
                                <Select
                                  onValueChange={(value) => {
                                    field.onChange(value);
                                  }}
                                  value={field.value}
                                >
                                  <SelectTrigger
                                    className={`bg-white rounded-[8px] border text-black ${
                                      fieldState?.error
                                        ? "border-red"
                                        : "border-gray1"
                                    } `}
                                  >
                                    <SelectValue placeholder="Velg husmodell" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white">
                                    <SelectGroup>
                                      {house && house.length > 0 ? (
                                        [...house]
                                          .sort((a: any, b: any) => {
                                            const aName =
                                              a?.Husdetaljer?.husmodell_name?.toLowerCase() ||
                                              "";
                                            const bName =
                                              b?.Husdetaljer?.husmodell_name?.toLowerCase() ||
                                              "";

                                            if (
                                              aName === "spesial" &&
                                              bName !== "spesial"
                                            )
                                              return -1;
                                            if (
                                              bName === "spesial" &&
                                              aName !== "spesial"
                                            )
                                              return 1;

                                            return aName.localeCompare(bName);
                                          })
                                          .map((house: any, index: number) => (
                                            <SelectItem
                                              value={house?.id}
                                              key={index}
                                            >
                                              {
                                                house?.Husdetaljer
                                                  ?.husmodell_name
                                              }
                                            </SelectItem>
                                          ))
                                      ) : (
                                        <>Ingen hus funnet!</>
                                      )}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="w-full col-span-2 hidden md:block"></div>
                  </div>
                  <div className="flex flex-col md:grid md:grid-cols-2 desktop:grid-cols-3 gap-4 lg:gap-6">
                    <div>
                      <FormField
                        control={form.control}
                        name="house.byggekostnader"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <p
                              className={`${
                                fieldState.error ? "text-red" : "text-black"
                              } mb-[6px] text-sm font-medium`}
                            >
                              Estimert{" "}
                              <span className="text-darkBlack font-bold">
                                totale byggekostnader
                              </span>{" "}
                              (oppstilling på neste side):
                            </p>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  placeholder="Skriv her..."
                                  {...field}
                                  className={`bg-white rounded-[8px] border text-black
                                          ${
                                            fieldState?.error
                                              ? "border-red"
                                              : "border-gray1"
                                          } `}
                                  type="text"
                                  inputMode="numeric"
                                  onChange={({ target: { value } }: any) =>
                                    field.onChange({
                                      target: {
                                        name: `byggekostnader`,
                                        value: value.replace(/\D/g, "")
                                          ? new Intl.NumberFormat(
                                              "no-NO"
                                            ).format(
                                              Number(value.replace(/\D/g, ""))
                                            )
                                          : "",
                                      },
                                    })
                                  }
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="w-full col-span-2">
                      <FormField
                        control={form.control}
                        name="house.Kommentar"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <p
                              className={`${
                                fieldState.error ? "text-red" : "text-black"
                              } mb-[6px] text-sm font-medium`}
                            >
                              Kommentar til banken:
                            </p>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  placeholder="Skriv her..."
                                  {...field}
                                  className={`bg-white rounded-[8px] border text-black
                                          ${
                                            fieldState?.error
                                              ? "border-red"
                                              : "border-gray1"
                                          } `}
                                  type="text"
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white pb-2 bottom-0 fixed w-[calc(100%-16px)] left-2">
            <div className="flex justify-end w-full gap-4 items-center  bg-white z-50 border border-gray3 p-3 md:p-4">
              <div onClick={() => setActiveTab(0)}>
                <Button
                  text="Tilbake"
                  className="border-2 border-primary text-primary text-base rounded-[34px] h-10 md:h-12 font-semibold relative px-6 md:px-8 py-[10px] flex items-center gap-2"
                />
              </div>
              <div id="submit">
                <Button
                  text="Neste"
                  className="border border-primary bg-primary text-white text-base rounded-[34px] h-10 md:h-12 font-semibold relative px-6 md:px-8 py-[10px] flex items-center gap-2"
                  type="submit"
                />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </>
  );
});
